//
export let wp_pages: any[] = [
    {
        header: `Eternal Dragons`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Eternal Dragons
_“As the circle completes and the dawn meets dusk, and as the moon covers the sun, the
answers will be revealed to the one Hero who stands at the crossing of Times. And she
will command Time.”_

<p style="text-align: right; font-style: italic;">(The Lost Prophecy)</p>

<p style="text-align: center;"><img style="max-width: 10%" src="Ornament_TwistedDragons.png" /> </p>

##Introduction
The epic new gaming series Eternal Dragons is brought to you by Trailblazer Games. Trailblazer Games is a Web3-native gaming studio formed by team consisting of three serial
entrepreneurs and veteran gaming industry leaders from the likes of King, EA and Microsoft,
who have experience in building large-scale and sustainable free-to-play economies. They are
united by a shared passion for creating engaging and entertaining gaming experiences.

The team's experience spans the design and production of award-winning games and franchises
like SimCity and Candy Crush – which touch billions of hearts across multiple platforms – but
also the building of platforms and tools for growth and engagement powered by data and AI,
as well as extensive blockchain development.

We see that we are now at the dawn of a new era in gaming. A new paradigm is emerging.
Web3 brings a new philosophy and crucial capabilities that can take gaming to a whole new
level when it comes to user inclusion, engagement and rewards for participation through
aligning incentives by recognising the truly symbiotic relationship that exists between players
and game makers in the co-creation of meaningful experiences. Gaming will be part of our
lives in a way we’ve never experienced before; more profound, meaningful and fun.

Out of this our philosophy has developed, where we believe in:
- Putting fun first – it all starts with fun. The entertainment value of the
core product has to be at the center of everything to drive engagement. It all starts
with the content.
- Inclusion and community – we listen to, and engage with the community. From early
access Alphas, to developer forums and resources, and to the future governance of
the Decentralized Autonomous Organization (DAO). We want to support and facilitate
the building of meaningful connections and a sense of belonging.
- Transparency – we build open economies transparently. By leveraging Web3
technology we cement the trust needed to trade confidently in a virtual economy that
is not centralized but co-owned by the broader community.
- Fairness – we want to distribute the value generated from games more fairly across
the broader community, making every player, co-creator and participant an active
stakeholder.

We see it as our mission is to **give the community a place to belong, play and prosper –
together**.

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        `
    },
    {
        header: `What is Eternal Dragons?`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#What is Eternal Dragons?
Eternal Dragons is a novel IP created by Trailblazer Games to tell the story of the Eternal
Dragons franchise, and power the creation of the initial four game experiences to be
developed over the next couple of years. The currently planned installments for the franchise
are:

1. Eternal Dragons - Prologue: Genesis
2. Eternal Dragons - Chapter 1: Strife
3. Eternal Dragons - Chapter 2: House of Dragons
4. Eternal Dragons - Chapter 3: War of Kings

In parallel with the development of these games, Trailblazer Games is developing a platform
that will power a new global economy where guilds can efficiently access capital, trade
assets and hire talent in order to compete, grow and succeed. This platform will be
developed and expanded in parallel with the games, integrated into the experience in order
to provide value directly to the players and the community by delivering a full vertical slice at
each stage of development.

Our goal is that the Eternal Dragons IP will transcend the original game series, powered by
the blockchain to provide future value and experiences within both other media and
entertainment formats as well as in the decentralized finance space.
In order to achieve that, we are laser focused on first and foremost creating fun game play
with mass market potential, packaged in an exciting fantasy universe of dragons and magic
where anything is possible, and where the players write the actual history!

###Value Proposition
Eternal Dragons allows the community and players to be part of writing history! Both in-game
and IRL. We believe that in the near future these concepts will merge and the lines will be
blurred between work and play, tangible and intangible, virtual and IRL.
In the Eternal Dragons universe, history will be written in several layers of abstraction, both
inside and outside the game world. In building this experience in collaboration between
developer and community, between creator and player, we are all together creating
something completely new. Be part of this history in the making:
- Create history in a new world as you uncover its mysteries
    - Your actions and performance will be saved in an immutable record. As the
world is shaped around you, your feats will forever be part of lore.
    - Game assets are NFTs, with a real provenance and recorded history of
performance. Only one Dragon in the whole world has won the most battles, it
could be yours!
    - Create real value through your engagement by earning game currency that
has real world value.
- Extended life time value of your personal investment
    - Bring your investments and performance with you into new experiences.
Dragon NFTs and other game resources on-chain will have value in new
game experiences and be transferable. Your previous engagement will be
rewarded when new products are released.
    - A fully fleshed out road map for an ecosystem of game experiences, built by a
team of seasoned veteran game developers, leveraging the full power of
Web3 capabilities:
        1. acquire and breed dragons in the Prologue: Genesis,
        2. bring your dragons into battle in Chapter 1: Strife,
        3. build a city and found a House for your alliance in Chapter 2: House of
Dragons, and
        4. put your arsenal to use and call your friends to arms in an epic
4X-experience in Chapter 3: War of Kings.
- Experience the emergence of a new paradigm in game development and
entertainment
    - Live the game developer fantasy. Give direct feedback to developers on
concepts, suggest your own ideas and actively participate and contribute
through competitions and votes.
    - Financial incentives are aligned with all participants in the ecosystem,
allowing for active contributors and participants to share the success.
- Help shape the future of work and decentralized finance
    - NFTs and blockchain technology allow for the gaming and finance spheres to
merge.
    - Rent out the NFTs you don’t use, hold $EDQ-tokens to participate in the revenue share,
    sell assets you don’t need, and invest in other top players and guilds whose performance
generates yield.

This whitepaper will first and foremost focus on the first two installments of the Eternal
Dragons franchise – as well as the definitions of the wider ecosystem supporting it’s
development, like the tokenomics, NFT definitions, governance etc. – and roughly sketch out
the concepts and intention for the full road map of later installments. The whitepaper will be
a living document to be updated as development progresses.        

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        
        `
    },
    {
        header: `Eternal Dragons - Prologue: Genesis`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Eternal Dragons - Prologue: Genesis

_“As the tiny almost fragile looking hatchling let its first gaze – ever so bright eyed – fall upon
the human watching over this precarious yet ceremonial event, they both instinctively knew
that this bond would never be broken.”_

<p style="text-align: center;"><img style="max-width: 10%" src="ornaments/Ornament_Egg04.png" /> </p>

##Introduction

---

_It is told that at the dawn of Time, Dragons roamed the world, and darkened the skies.
Legend has it that they lived in a strange equilibrium of fierce peace and harmony, in
unbreakable bonds with the elder races._

_Then, something horrible happened. But all memory is lost. The Dragons disappeared,
banished from memory, turned into myth. Only fragments and trails of clues to their history
exist across the continents, for those who look._

_Now, strange orbs have appeared. Are these the Dragon eggs of old, lost in Time and only
reappearing now? Or are they the sign of the Dragons just being in hiding, biding their time
to reemerge? Clues lie riddled across the lands, beneath the mountains, in the seas and
skies; remnants, lost memories of the elders. The one who finds the answers to what
happened with the Dragons, will become all-powerful and able to harness and wield the full
power of Time._

_Now is the time of re-birth to bring the mighty Dragons back!
Whomever the re-incarnated Dragons latch on to at their hatching ceremony, will forever
form an unbreakable bond with them. A bond only severable through the most powerful of
magic._

_This, is the Time of Genesis. The birth of a new Dragon age has come!_
 
---

<p style="text-align: center;"><img style="max-width: 10%" src="ornaments/Ornament_Egg04.png" /> </p>

##The Eternal Dragons NFTs
The first installment of the Eternal Dragons series will be the release of an NFT collection of 10.000 unique Genesis Eternal Dragons and a breeding game that will populate the universe with Descendant Eternal Dragons. These will be two distinct collections of NFTs: One fixed set of 10.000 Genesis Eternal Dragons, representing the immortal pantheon of the Eternal Dragons universe, and one dynamic collection of Descendant Eternal Dragons.

The purpose of this is to seed the Eternal Dragons universe with valuable NFTs, in a way that rewards early adopters and advocates in the community, while making sure there is a low threshold entry point for newcomers to the Eternal Dragons games.

The Genesis Eternal Dragons NFTs are the original set of Dragons hatched from the first spawn of Dragon Eggs discovered. These are unique and can never be replicated.

As the Eternal Dragons NFTs are minted, their DNA will be encoded on the blockchain, permanently putting their complete DNA with all traits completely on chain. These Eternal Dragons have the chance to have both special abilities and hidden secrets that will only be uncovered later. They will also have unique and special attributes, exclusive to the Genesis collection, so they will have a completely unique look and several trait variations that cannot be inherited.

The Genesis Eternal Dragons collection, represent a core piece of the entire Eternal Dragons universe, IP and franchise. Holding an Eternal Dragons NFT will not only give you benefits in the short term, like early access to the first game releasing later this year, but will also provide you with long term benefits across all the games and entire ecosystem. It will be your very own, extremely powerful creature with extended utility across all the coming game chapters.


Some of the unique benefits and capabilities of the Genesis Eternal Dragons NFTs are:
- They will have unique and special attributes with unique appearance, Eternal genes that cannot be inherited and thus are unique only to the first Eternal Dragons generation.
- They will have some unique in-game abilities and increased in-game power.
- They have uncapped breeding revenue potential.
- They will receive Eternium passively from descendants.

Further, Eternal Dragon NFT holders will have special standing in the community and get
special perks, like
- Item Airdrops – NFT holders will get Eggs airdropped ahead of the release of the breeding game.
- Token Airdrops – NFT holders will also get airdrops of Eternium, the utility token required for breeding.
- Access to pre-sales of both future NFTs within the Eternal Dragons series, and the $EDQ token.
- Early access to test out Eternal Dragons – Chapter 1: Strife.
- Access to exclusive developer Discord channels.
- Possibility to give direct feedback on development and participate in the creation and evolution of the Eternal Dragons universe.
- Becoming an active part of making history with the most innovative application of blockchain technology within gaming, in the most exciting novel IP so far!

The Eternal Dragons NFTs will be sold through a series of NFT sales.

##Affinity, Class & Rarity
A Genesis Eternal Dragons NFT can have any of 12 affinities, 9 classes and 5 rarities. A Dragon’s class will define its general look and playstyle, and it’s affinity will define its primary color. The power will be a function of the rarity of its traits. Further they will have several different individual traits, and special skills to be used in battle.

##Breeding


The Genesis Eternal Dragons NFTs are the original Dragons populating the Eternal Dragons world. Each Dragon will spawn from an Egg after minting, and has a completely unique DNA.

In order to breed Descendant Eternal Dragons, a player will only need an Egg and Eternium, but do not have to own any dragons themselves. They only need access to the DNA, or Dragon Blood, of two parent dragons.

Players will earn Eggs and Eternium as rewards when progressing and reaching achievements in the game, and this is all that is needed in order to breed new dragons. Anyone owning a dragon (either an Genesis Eternal Dragon or an Eternal Dragon Descendant) can put their dragon up for siring, i.e. make their dragon available for breeding. Then, a user who has an Egg, can choose any two available dragons in the market and use those as parents for their Egg. The cost for breeding is then paid in Eternium. Eternium is in this ceremony required as a catalyst to restore balance, as it represents the Dragons’ life force. Even if Eternium can take many different shapes and forms, only the Dragons can harness its raw power properly and safely. The Eternium offering made in this ceremony will go to the NFT-owners of the parents, with a portion also going to the DAO.

In this way we can give as much value to owning just one Genesis Eternal Dragon as possible, while not gating access to the game and the breeding in the long run and while providing meaningful rewards through gameplay. We believe that one of the biggest benefits of this system is that the inflow of new dragons is controlled by the availability of Eggs and is dictated more by the need for new dragons, rather than by the inflated supply created by free-for-all breeding that pushes out too many dragons into the system too quickly. This is a win-win, since this system helps keep a good equilibrium between supply and demand, keeping prices healthier in the long run.

This system provides a way to passively participate in the value creation in the ecosystem, also for community members who don’t necessarily have time to play or manage their game assets actively. They only need to buy and hold NFTs, garnering some of its benefits with a minimum amount of management. This enables
<ol type="a">
  <li>providing value and benefits to the holders of the NFTs even if they have limited time to interact with the game, while</li>
  <li>making sure the supply of dragons for the game is not impeded by e.g. passive NFT holders that might not interact frequently with the game mechanics.</li>
</ol>

This system of two distinct NFT collections with demand driven breeding will provide a healthy ecosystem of NFTs and assets of both high and low value for all types of participants with different needs.

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        
        `
    },
    {
        header: `Eternal Dragons - Chapter 1: Strife`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Eternal Dragons - Chapter 1: Strife

_“The battle was at an impasse, a stand-off with both sides awaiting the other’s move… until,
slowly the leaves started rustling; then there was a gust of wind and suddenly a roar was
heard as a Wind Dragon swept down bellowing with thunder, throwing the opponent’s
minions to the ground, deafened and cowering in fear. In that instant, the battle was over,
and the spoils were ripe for the taking. Only the elegy for the fallen softly sounded in the
deafening silence.”_

<p style="text-align: center;"><img style="max-width: 17%" src="ornaments/Ornament_DragonSwooping.png" /> </p>

##Introduction

---

_The hunt is on, and the battle has begun for the resources, fragments and clues to the
answers as to why the Eternal Dragons ever disappeared. Not to mention why they’ve
reappeared._

_What has brought this about? As it’s written in the Lost Prophecy, the one who stands at the
crossing of Times at the next Concurrence will be able to command Time. As hotspots of
Eternium appear across the lands, Heroes seeking answers have to battle for the precious
resource._

_Bring your prowess into the battlefield, to win the answers you seek, in the quest for
immortality._

---

<p style="text-align: center;"><img style="max-width: 20%" src="ornaments/Ornament_DragonSwoopingDown.png" /> </p>

##The Battle
The core gameplay of Eternal Dragons – Chapter1: Strife is based on the fun and engaging
gameplay with a proven track record in auto chess and similar auto battler games. These
games have a good balance of strategy and tactics, and skill vs luck as well as several
levers to control this balance.

###Basics
In the core battle game, the player is pinned versus other players in the arena, alternatively a
set of NPC heroes or bosses in PvE mode.

The arena is where the players place their dragons, as well as their minions. The minions
are units that fill out the battle roster, if the player hasn’t collected enough dragons. These
minions will always be less powerful than the dragons.

Each battle is fought by the player placing units from a randomized roster into the battlefield,
for a varying cost of activity points. The player can merge two or more
of the same type of unit, in order to increase it’s tier and make it more powerful for the
duration of the battle. Once all units have been placed, the battle will automatically ensue
and play out without further player interactions. This loop repeats between every round of
battle, with an increasing amount of action points available to the player, until only one
player’s hero remains.

###Affinity Synergies
All Dragons and minions have an affinity and a class. Synergies are powers that
affect the player’s units’ ability in battle positively, or that affect the enemy negatively. The
actual effects can have different triggers, but they come into play only when the player has
enough unique units of the same affinity in the arena. Each synergy gets an
improved effect with each threshold of number of unique units reached.

###Leagues and Tournaments
As players improve, acquire more Dragons and level up their Dragons, they’ll become more
and more powerful. That means they can climb the ranks and fight in the really high stakes
competitions where the rewards are more valuable, and can even be the Eternal Dragons
token $EDQ.

The core gameplay is also designed to suit Esports-style tournaments, which can even be
initiated by players. This could e.g. allow players and token holders to set an entry fee to a
tournament of their choice, and invite competitors to battle it out for the rewards pool.

###Leveling up Dragons
The Dragons in Eternal Dragons have a natural power progression curve that is defined by
their battle experience and Eternium investments.
One of the best ways for a player to increase their net value is to play a lot with their
Dragons and reinvest their Eternium winnings in their Dragons’ power progression. This is
expected to significantly increase the market value of said Dragons.


<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        
        `
    },
    {
        header: `Game Economy`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />


##Game Economy
The economy in and around the Eternal Dragons universe is an exciting hybrid model where
the boundaries between our world and the game universe are at the same time both fluid
and fuzzy, as well as tangible and real. This can be said since we can both put in as well as
take out value that is affected by our time, actions and performance in-game, from the game
world. The most exciting aspect of this symbiotic relationship between the virtual and the
tangible worlds, is that it tests the status quo and has the potential to completely disrupt our
views on what constitutes entertainment or investment, work or leisure. In Eternal Dragons
the catalyst for this paradigm shift is Eternium.

<p style="text-align: center;"><img style="max-width: 17%" src="ornaments/eternium_golden_dragon.png" /> </p>

###Introduction

---

_Eternium - the essence of Time in its purest form, the magical element imbuing and flowing
through everything, that allows magicians to manipulate Time and gives Dragons their raw
power and life force. The bedrock of the Eternal Dragons universe, of the Eternal Dragons themselves._

---

<p style="text-align: center;"><img style="max-width: 17%" src="ornaments/eternium_golden_dragon.png" /> </p>

###Eternium – The Utility Token of the Eternal Dragons Universe

Eternium comes in many forms and different physical states, can infuse almost anything and
is the core resource powering the Eternal Dragons universe. It will be necessary for most of
the value creation happening in the universe, like e.g. breeding and Dragon upgrading.
Eternium is a variable supply token and in order to balance the game economy the developers
reserve the right to mint and burn Eternium as needed, as well as to adjust the prices of
various activities Eternium is used for, and to tweak the sources that reward Eternium.

###How to Acquire Eternium?
- Eternium will be traded freely on <span style="color: blue; text-decoration: underline;"><a href="https://portal.projectserum.com/#tabSection" target="_blank">compatible trading platforms</a></span>.
- The initial liquidity of Eternium will be provided by the Treasury.
- Eternium will be airdropped to Genesis Eternal Dragons NFT holders ahead of the release of the breeding game.
- Players will earn Eternium by completing quests in the game (like playing PvE,
winning PvP matches, performing certain actions etc) as rewards, and as rewards for
extraordinary achievements.

###Uses for Eternium
Some of the uses for Eternium are:
- Breeding
- Leveling up Dragons
- Healing Dragons
- Competition entry fees
- Many more uses in the coming chapters of Eternal Dragons (see the Roadmap
section for examples)

##Other Resources

###Dragon Feed
Dragon Feed is an off-chain resource that is needed to enter battle in the Eternal Dragons
universe. Once used, it replenishes with time in order to pace game play, limit burnout
potential and serve as a return driver for players. Some of its purposes include:
- Content pacing
- Increased game longevity
- Session pacing
- Burnout protection
- Exploit and abuse protection

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        
        `
    },
    {
        header: `Tokenomics and Economy Launch Plan`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Tokenomics and Economy Launch Plan

Notes on the tokenomics design:
- The main purpose is to align motivations and incentives of all participants in the
ecosystem as best as possible.
- These participants can on a high level be divided into: 1) Players 2) Investors 3)
Developers
- The motivations of these participants are fluid and overlapping from time to time.
Most of the direct play-to-earn value created in the Eternal Dragons ecosystem is expected
to stem from the Eternium that is directly integrated into the game economy, whereas the
Eternal Dragons token $EDQ should more reflect the overall performance of the ecosystem
economy as a whole, and be a means of sharing and distributing that value to backers of the
project in the community.

<p style="text-align: center;"><img style="max-width: 17%" src="ornaments/EDQ_token.png" /> </p>

##$EDQ – Eternal Dragons Token

$EDQ is a fixed supply token that will be used for distribution of revenue
and is expected to give holders voting power over certain aspects of the games and ecosystem.
Exact allocations and unlock schedules are still to be announced.

##Utility

###Stakeless Revenue Distribution

Our intention is to fairly distribute the value created between community, developers, and investors.
We plan on achieving this through an innovative stakeless system where $EDQ-holders will be able
get rewards, just by holding the $EDQ-token. Through this ground breaking stakeless system
$EDQ-holders will on regular basis be rewarded with network tokens, i.e. $SOL, earned by the DAO.

Some of the many benefits are that holders don't need to stake their tokens, and that we're not creating an
inherently inflationary economy by just rewarding more of the same token for everyone that stake.
Holding an $EDQ-token is just like owning a small piece of the entire Eternal Dragons franchise.

##Governance

The Decentralized Autonomous Organization (DAO) controls all aspects of the blockchain
infrastructure of Eternal Dragons. Initially the DAO will be administered by the developer.
The long-term goal is to eventually hand-over the governance of the DAO to
$EDQ-holders.

$EDQ-holders will then be able to deposit their tokens and acquire voting power to
take part in the governance of the DAO. The longer the deposit period, the larger the voting
power. The DAO will have power over some aspects of the ecosystem, like e.g.:

- Controlling the vesting schedule
- Updating fees for royalties in the ecosystem
- Issuing new tokens or NFTs
- Some in-game functionalities like
    - Entry fees for some competitions
    - Badge creation, and potentially other user generated NFTs
- Vote on new proposals

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        
        `
    },
    {
        header: `Business Model`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Business Model

##Monetization and Redistribution Model
The sources of revenue for the DAO are the following:
- Income from primary sales of NFTs in the form of $SOL
- Fees and royalties from secondary transactions of NFTs in the form of $SOL
- Eternium spent in the game and sold for $SOL.

The proceeds mentioned above will be kept in the DAO Treasury. Based on market
conditions and reinvestment strategy, a decision will be made by the DAO to distribute an
adequate percentage to $EDQ-holders, and to pay for the development and operation of the
game.

In addition, the DAO may also take the decision to buy, sell or burn tokens to contribute to
price stability and long-term value creation.

<img style="max-width: 100%; margin-bottom: 20px" src="UtilityTokenFlow.jpg" />

<img style="max-width: 100%" src="NFTflow.jpg" />

##Growth Model
The most important factor for growth will be the community, where incentives for growth are
aligned throughout the ecosystem. Some of the actions that will be taken to bootstrap the
community growth are:
- Community Management
    - Setup and actively manage relevant channels to engage with the community
globally including Twitter and Discord.
- Influencer marketing
    - Identify and recruit relevant influencers with focus on credibility and genuine
interest in the product and vision.
- Content strategy
    - Deliver a pipeline of content to share the progress with the community and
get their feedback.
- Product co-creation
    - Request product input and feedback, allowing the community to vote on
initiatives.
- Whitelists
    - Actions supporting community growth, awareness and engagement in the
community will be rewarded with early access to private pre-sales
- Airdrops
    - Specifically selected wallets can get airdrops directly to incentivise
engagement with the project and community
- Referral programs
    - Early adopters and core community members that help spreading awareness
will be rewarded
- PR
    - A series of press releases will go out with news surrounding the company
- Advisors and influencers
    - Close advisors and influencers will leverage their networks for growth
purposes

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        `
    },
    {
        header: `Technology`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Technology

The Eternal Dragons token $EDQ is an SPL token, i.e. based on the Solana Program
Library, issued on the Solana network at the mint address:
>EDQn19p9niG6c9Gae7qtVYA9FCejRZ9u4mN2ACgcfNpB.

The Eternium token is an SPL token issued on the Solana network at the mint address:
>ETER9jTizmKFKLtW55jMd9MaBWhR2UZpUx48yR3Jixrx.

The Eternal Dragons DAO address is located at:
>9pLvqBe2CxC4HXQD5UQ7YLpQUqC5A9SqU4kNqw9inbNQ.

All blockchain assets in the game (like Dragons) are SPL tokens enriched with:
- Metaplex metadata
- Metaplex master edition
- Trailblazer data

Solana is a recent and booming ecosystem, currently ranked no. 5 in overall chain
capitalisation. Solana’s technology helps solve some of the common issues that blockchain
games have been facing, like e.g. transaction costs and scalability. This means Eternal
Dragons will not need to run on a sidechain or utilize a level 2 solution, but will make the
user experience more accessible and offer the power of the vast Solana ecosystem to the
users at a low cost.

Solana allows us to:
- keep transaction costs very low
- settle transactions in mere seconds
- eliminate custodial risk, users keep their private keys
- scale the game
- use a level 1 chain and access all of the solana ecosystem
- add new functionalities to the game assets easily

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        `
    },
    {
        header: `Roadmap`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Roadmap

This exciting and ambitious road map reflects the current goals and timelines for developing
the Eternal Dragons franchise. Like all major game projects, the road map and plans are
subject to changes in time and scope, especially considering our commitment to involving
the community early on to shape the future of the Eternal Dragons IP. As such any changes
or significant delays would of course be communicated to the community as early as
possible.

<p style="text-align: center;"><img style="max-width: 30%" src="ornaments/Ornament_DragonSwimming.png" /> </p>

##Early 2022
- Website launched and project announced
- Whitepaper published
- Discord and social channels opened
    - Whitelist competitions started

##Mid 2022
- Eternal Dragons NFT presale
- Eternium Launch
- Perks and benefits, like Airdrops, for early holders of Eternal Dragons NFTs
- Eternal Dragons – Prologue: Genesis
    - Breeding game released
- Eternal Dragons – Chapter 1: Early access
    - Early access limited gameplay open for Eternal Dragons NFT holders
- Eternal Dragons – Chapter 2: Concept and design

##Late 2022
- Private $EDQ sale
- Public $EDQ sale
- Chapter 1: Live on all platforms
- Chapter 2: Development started; presale

##2023
- Chapter 2 Release
- Eternal Dragons – Chapter 3: War of Kings

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        `
    },
    {
        header: `Eternal Dragons - Chapter 2: House of Dragons`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Eternal Dragons - Chapter 2: House of Dragons

_"Upon the strategically invaluable isthmus of Kandaria, the budding town of Kandar was
sprawling out from the ancient stronghold, the infamous seat of the powerful House of
Kandar”_

<p style="text-align: center;"><img style="max-width: 10%" src="Ornament_TwistedDragons.png" /> </p>

##Introduction

---

_Behold the budding hamlets, villages and towns;
sprawling across the continents like the rising tide, evolving into cities of unfathomable
power, fueled by the rising Houses of Dragons._

---

<p style="text-align: center;"><img style="max-width: 10%" src="Ornament_TwistedDragons.png" /> </p>

##The City

_Eternal Dragons – Chapter 2: House of Dragons_ will be centered around the burgeoning new
civilisations rising from the ashes of the elders, spurred on by the reemergence of the mighty
Dragons and the harnessing of Eternium. Players will be able to put their hard earned
resources from Chapter 1: Strife to use in a new setting, with new challenges, building
civilizations and alliances.

The Dragon NFTs will be valuable to seed the land and harness its riches through affinity specific bonuses when
building the city. Eternium can also be invested in new assets like e.g.:
- Town halls, Guild Houses and other buildings
- Resource producers and mines
- Decorations

The resource management system will also allow for specialization, e.g. through the boosts
provided by the specific Dragon NFTs a player is holding, creating an asymmetric resource
economy. This means trading and collaboration is of the essence to be successful. The best
way to collaborate in Eternal Dragons – Chapter 2: House of Dragons is to found a House.

##The House

A House is the core pillar upon which alliances are founded. In Eternal Dragons – Chapter 2:
House of Dragons you can establish a House, fashioned after your own devices, where allies
can share resources as well as spoils and loot.

Technically, a House is a guild intended to be set up as a sub-DAO of the Eternal Dragons DAO, and
supported by the evolution of the Player Funding Platform, part of which is branded the Eternal Book.
This allows a House to be automatically set up in a trustless way with specific rules and regulations
for how e.g. revenue shares will be split. It will also help a House to seek additional investments as well
as attract other high performing players, since the players’ and Houses' performance will be
completely transparent in the Eternal Book.

Now is the time to tie strong bonds and get ready. War is coming.

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        `
    },
    {
        header: `Eternal Dragons - Chapter 3: War of Kings`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Eternal Dragons - Chapter 3: War of Kings

_"First, their silhouettes were clearly discernible against the clear and bright midday sun, but
as the Dragons grew closer, the sky darkened as their shadows completely covered the
ground, only to suddenly blind any witnesses as the forges of their bellies erupted and let the
eternal fire rain out of the skies."_

<p style="text-align: center;"><img style="max-width: 17%" src="ornaments/Ornament_DragonSwooping.png" /> </p>

##Introduction

---

_War is coming. Kings of the Houses pinned against each other. Who will uncover
the answers to the questions of old and unlock the past, present and future through the
power and magic of Time?_

---

<p style="text-align: center;"><img style="max-width: 15%" src="ornaments/Ornament_Egg07.png" /> </p>

##The World
_Eternal Dragons – Chapter 3: War of Kings_ is where the previously formed alliances will be
put to the test against each other in a 4X setting, pinned against each other in the search for
the answers and the battle for power.


The Houses that have prepared the best, will be poised for greatness. But they should not
rest on their laurels, but beware upstarts and usurpers that are gunning for them. Only the
one that uncovers all the answers, will truly be able to wield the full power of Time.
In this installment there will be even more use cases for Eternium, like:
- Creating potions
- Spell casting
- Teleportation

Land will also play a specific strategic role as the Heroes venture out into the world to
continue their search for answers through exploration, mining and battle. The strategic
advantage offered by different terrain and land plots, further deepens the value of optimally
organizing alliances as the role of specialization becomes even more important. The best
prepared House will prevail.

##The War
The Houses of Dragons will battle for glory, in the pursuit of the answers to what initially
happened to the Eternal Dragons, and why they reappeared.

Heroes will lead armies into battle and each player can control several armies that will travel
the world in their search. Players can prove their mettle in battle, as well as show off their
prowess,e.g. with hero skins, mounts or familiars.

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        `
    },
    {
        header: `Player Funding Platform`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Player Funding Platform

At the core of all Trailblazer games and products is the Player Funding Platform (PFP for
short). This platform will power a new global economy where guilds and players alike can
efficiently access capital, trade assets and hire talent in order to compete, grow and
succeed. It is designed to support and facilitate these interactions between the different
users in the Trailblazer ecosystem.

As part of the road map, the PFP will be implemented as a foundational layer of the vertical
slice supporting each new game experience, and not as a standalone platform. The solution
will be abstracted and generalizable, but our strong belief is that the technology first and
foremost has to serve proven use cases for the end user.

Players, guilds and investors will be able to interact through this fully integrated platform.
Players and guilds will e.g. be able to look for funding from investors, who in turn through the
player and guild profiles, guaranteed by the trustless on-chain Eternal Book, can
decide where to best invest. The Eternal Book records things like a player’s or guild’s badges,
medals, championship results, history and stats in their profile on-chain. The Eternal Book can
also contain entries for the history and provenance of specific NFTs, like the performance of
the rarest or most powerful Dragons in the world.

The PFP is also intended to automate large parts of the process of creating a guild and
launch it as a Decentralized Autonomous Organizations (DAO), through a seamless
on-chain creation in-game. Through this interface users can also rent NFTs or invest
tokens in guilds, which in turn aligns incentives through trustless and transparent plans for shared resource pools
and a revenue share of winnings. The platform automates the income distribution among the guild members, helping
everyone play and prosper together.

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        `
    },
    {
        header: `Disclaimer`,
        markdown: `

<img style="max-width: 100%" src="Ornament_SectionBreakTwistedDragons.png" />

#Eternal Dragons’ White Paper Disclaimer

This document (the “**White Paper**”) is made available for informational purposes only and may be subject to change. This White Paper does not constitute an offer or solicitation to sell securities or any other regulated product. No information or opinions presented herein are intended to form the basis for any purchase or investment decision, and no specific recommendations are intended.

Eternal Dragons Operations Ltd (“**Eternal Dragons**”) is creating non-fungible tokens (“**NFTs**”) which can function within games, be traded between players, be used to earn in-game currencies and to access early versions of games. 
Eternal Dragons does not grant any rights, express or implied, other than the right to use its NFTs on its platform or games. In particular, Eternal Dragons expressly denies any ability of the NFTs to represent or confer any ownership right or stake, share, security, voting or equivalent rights, or any right to receive future revenue shares, intellectual property rights, economic interest or any other form of participation in or relating to any Eternal Dragons’ product, Eternal Dragons and/or any of its affiliates. The NFTs do not represent an entitlement to, or provide exposure to the value of, any underlying property, nor do they represent a claim against Eternal Dragons, whether in the form of debt or otherwise. 

Eternal Dragons does not make or intend to make, and hereby disclaims, any representation, warranty or undertaking in any form whatsoever to any entity or person, including any representation, warranty or undertaking in relation to the truth, accuracy, and completeness of any of the information set out in this White Paper.

To the maximum extent permitted by the applicable law, Eternal Dragons, its founders, team members and its affiliates shall not be liable for any direct, indirect, special, incidental, consequential or other losses of any kind, in tort, contract, equity or otherwise (including but not limited to loss of revenue, income or profits, and loss of use or data), arising out of or in connection with any acceptance of or reliance on the information in this White Paper. All content in this White Paper is provided “as is” and without warranty or representation of any kind. 

To the maximum extent permitted by the applicable law, Eternal Dragons:
<ol style="list-style-type: none" type="a">
  <li>a) expressly disclaims all implied warranties as to its NFTs, including, without limitation, implied warranties of merchantability, fitness for a particular purpose, title and non-infringement;</li>
  <br/>
  <li>b) does not represent or warrant that the NFTs are reliable, current or error-free, or that technical defects in the NFTs will be corrected;</li>
  <br/>
  <li>c) cannot and does not represent or warrant that the NFTs or the delivery mechanism for NFTs are free of viruses or other harmful components; and</li>
  <br/>
  <li>d) expressly disclaims all liability arising out of or relating to the NFTs, or the use of, or inability to use the NFTs.</li>
</ol>

Information received through the White Paper should not be relied on for personal, legal, investment or financial decisions. This White Paper does not constitute an offer or solicitation to sell any cryptocurrencies, virtual coins, securities or other type of regulated product. No information or opinions presented herein are intended to form the basis for any purchase or investment decision, and no specific recommendations are intended. Accordingly, this White Paper does not constitute investment advice, recommendation or counsel or a solicitation for investment, nor does it amount to legal, financial or tax advice and/or opinion.

Any such offer or solicitation will be made only by means that are in compliance with applicable securities and other laws. As such, this White Paper does not constitute or form part of, and should not be construed as, any offer for sale or subscription of, or any invitation to offer to buy or subscribe for, any securities or assets, nor should it or any part of it form the basis of, or be relied on in any connection with, any contract or commitment whatsoever. The NFTs should not be purchased for speculative or investment purposes, and purchasers should only purchase the NFTs if they fully understand the nature of the NFTs and accept the risks inherent to them.

This White Paper contains forward-looking statements and descriptions of goals and objectives. The events or circumstances reflected in forward-looking statements might not occur. Forward-looking statements can be identified by the use of forward-looking terminology such as “believes,” “potentially”, “expects,” “may,” “will,” “should,”“seeks,” “approximately,” “intends,” “plans,” “estimates” or “anticipates” or the negative of these words and phrases or similar words or phrases. Forward-looking statements can also be identified by discussions of strategy, plans or intentions. 

Although these forward-looking statements and stated goals and objectives are based upon assumptions and research, actual results of operations and achievements may differ materially and adversely from the statements, goals and objectives set forth in this White Paper. The information contained in this White Paper is based on assumptions and projections as to future events that are inherently uncertain. 

Further, it can be expected that one or more of the assumptions or projections on which such information is based will vary significantly from actual events, and such variances will likely increase over time. No representation or warranty, express or implied, is made and no legal or other liability is accepted as to actual future events.

The information contained herein is presented as of the date hereof. NFTs, as envisaged in this White Paper, are constantly updated. Future developments may affect the functionality of the NFTs such that they differ from the description set out in this White Paper. Eternal Dragons or its affiliates reserve the right to modify any of the terms of the White Paper at any time. 

The NFTs rely on the Solana blockchain, which is still in a relatively early development stage and is unproven for this purpose. Any malfunction, flaws, breakdown or abandonment of the Solana blockchain may have a material adverse effect on the NFTs. Furthermore, developments in cryptographic technologies and techniques or changes in consensus protocols or algorithms could present risks to NFTs.

References in this White Paper pertaining to specific networks, companies, and/or use cases are for illustrative purposes only. The use of any network or company name does not imply any endorsement of/by, and/or affiliation with the said parties.

Appropriate precautions should be undertaken to secure the NFTs. NFTs can be subject to hacking, exploitation, expropriation and/or theft. Hackers or other malicious groups or organisations may attempt to interfere with our systems or the Solana network in various ways, including malware, consensus, sybil, phishing, smurfing, and/or denial-of-service (DDoS) attacks. There may also be attempts to remove the party’s access or control over their cryptographic functions via spoofing and smurfing hacks. Should such an event occur, Eternal Dragons or its affiliates do not guarantee a remedy, and holders of NFTs are not guaranteed any remedy, refund, or compensation.

NFT holders have no rights to require Eternal Dragons or its affiliates to redeem their NFTs at any value or at any time. The value of the NFT will fluctuate, perhaps significantly over short periods of time, in accordance with various market forces and parameters, technical advancements, and regulatory, economic and political factors, and is not guaranteed by Eternal Dragons or its affiliates in any way.

The NFTs are uninsured unless a holder specifically obtains private insurance to insure them.  Thus, in the event of loss or loss of utility value, there is no public insurer or private insurance arranged by Eternal Dragons or its affiliates to offer recourse to holders.

The tax treatment and accounting of NFTs is uncertain and may vary amongst jurisdictions. Holders of NFTs must seek independent tax advice in connection with NFTs, which may result in adverse tax consequences.

This White Paper in any part thereof and any copy thereof must not be transmitted to any country where distribution or dissemination of these documents and its information is prohibited or restricted. No regulatory authority has examined or approved to this date any of the information set out in this document. The publication, distribution or dissemination of these terms do not imply that the applicable laws, regulatory requirements or rules have been complied with.

This White Paper shall be governed by and construed in accordance with the laws of Singapore.

<img style="max-width: 100%" src="Ornament_SectionBreakHead.png" />
        `
    }
]
